import { escapeRegExp } from "lodash-es";

import { JSONValue } from "src/datasets/DatasetTable/types";

export const PII_MASKED_TOKEN = "<masked>";

export type ExecutedPromptVariables = {
  [expression: string]: { value: JSONValue; masked?: boolean };
};

export type ExecutedPromptSegment =
  | { text: string; isVariable: false }
  | { text: string; isVariable: true; expression: string; isMasked: boolean };

const stringifyValue = (value: JSONValue): string =>
  typeof value === "string" ? value : JSON.stringify(value);

/**
 * Splits a prompt template into literal and substituted segments, replacing
 * every occurrence of each expression (e.g. "$data.applicant.name") with its
 * runtime value.
 */
export const interpolatePromptTemplate = (
  template: string,
  variables: ExecutedPromptVariables,
): ExecutedPromptSegment[] => {
  // Longest-first so overlapping expressions ("$data.a.b" vs "$data.a")
  // resolve to the most specific match.
  const expressions = Object.keys(variables)
    .filter((expression) => expression !== "")
    .sort((a, b) => b.length - a.length);

  if (expressions.length === 0) {
    return template === "" ? [] : [{ text: template, isVariable: false }];
  }

  // The negative lookahead rejects matches followed by more expression
  // characters (unicode letters/digits/underscore, "[", or a "." that
  // continues a path), so a recorded key that is only a prefix of a longer
  // expression in the template (e.g. "$data.name" inside "$data.name_full"
  // or "$data.report" inside "$data.report.status") is left untouched
  // instead of corrupting the reconstruction.
  const matcher = new RegExp(
    `(${expressions.map(escapeRegExp).join("|")})(?![\\p{L}\\p{N}_\\[]|\\.[\\p{L}\\p{N}_])`,
    "gu",
  );

  // Splitting on a regex with a capturing group keeps the matches in the
  // result at odd indices: [literal, match, literal, match, ..., literal].
  // A single pass over the template means substituted values are never
  // re-scanned for expressions.
  return template
    .split(matcher)
    .map(
      (part, index): ExecutedPromptSegment =>
        index % 2 === 1
          ? {
              text: stringifyValue(variables[part].value),
              isVariable: true,
              expression: part,
              // Token fallback covers decisions recorded before the
              // backend started sending the masked flag.
              isMasked:
                variables[part].masked === true ||
                variables[part].value === PII_MASKED_TOKEN,
            }
          : { text: part, isVariable: false },
    )
    .filter((segment) => segment.isVariable || segment.text !== "");
};

export const compilePromptTemplate = (
  template: string,
  variables: ExecutedPromptVariables,
): string =>
  interpolatePromptTemplate(template, variables)
    .map((segment) => segment.text)
    .join("");
